import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { nanoid } from "nanoid";
import { STORYBOARD_WS_URL } from "@/constants";
import { StreamingAudioChunk } from "pages/media/components/storyboard/editor/types";
import { authService } from "@/services/authService";
import { SectionUpdate, TScript } from "pages/media/types";
import {
  StoryboardMessage,
  WebSocketMessage,
} from "pages/media/components/chat/types";
import { Maybe } from "types";

type TTSChunkHandler = (chunk: StreamingAudioChunk) => void;
type MessageHandler = (message: StoryboardWsMessage) => void;

export interface TTSRequestOptions {
  sectionId?: string;
  slideId?: string;
  text?: string;
  voiceId?: string;
  /** Client-only cache/playback key, never sent over the wire. Overrides
   * slideId/sectionId for local dedup when neither is a real backend id. */
  cacheKey?: string;
}

export interface TTSCacheEntry {
  text: string;
  chunks: StreamingAudioChunk[];
  isComplete: boolean;
  voiceId?: string;
}

export interface StoryboardWsMessage {
  action?: string;
  query_id?: string;
  section_id?: string;
  speech?: string;
  chunk_index?: number;
  is_final?: boolean;
  status?: string;
  [key: string]: unknown;
}

interface StoryboardWsState {
  ws: WebSocket | null;
  isConnected: boolean;
  isConnecting: boolean;
  isBusy: boolean;
  // TTS chunk subscribers by query_id
  ttsSubscribers: Map<string, TTSChunkHandler>;
  // General message handlers
  messageHandlers: Set<MessageHandler>;
  // Message history for tracking requests/responses
  messageHistory: StoryboardMessage[];
  // Reconnection state
  config: InitConfig | null;
  connectionAttempts: number;
  // Epoch ms when the current connection opened; 0 while disconnected
  connectedAt: number;
  // TTS cache by playbackId (sectionId or slideId)
  ttsCache: Map<string, TTSCacheEntry>;
  // Maps queryId to playbackId for caching incoming chunks
  queryToPlaybackId: Map<string, string>;
}

interface InitConfig {
  organizationId: string;
  documentIds: string[];
  projectId: string;
  storyboardId: string;
}

interface StoryboardWsActions {
  init: (config: InitConfig) => void;
  disconnect: () => void;
  // TTS
  requestTextToSpeech: (options: TTSRequestOptions) => string | null;
  subscribeToTTS: (queryId: string, handler: TTSChunkHandler) => () => void;
  // Script actions
  requestGenerateScript: (
    storyboard: unknown,
    sectionId: string
  ) => string | null;
  requestImproveScript: (
    sectionId: string,
    script: TScript,
    message: string
  ) => string | null;
  // Section actions
  requestCreateSection: (topic: string, storyboard: unknown) => string | null;
  requestUpdateSection: (
    sectionId: string,
    sectionUpdate: SectionUpdate
  ) => string | null;
  requestRegenerateSection: (
    sectionId: string,
    topic: string,
    storyboard: unknown
  ) => string | null;
  requestRetrieveSectionData: (id: string) => string | null;
  // Metrics
  requestComputeMetrics: (storyboard: unknown) => string | null;
  // General message subscription
  addMessageHandler: (handler: MessageHandler) => () => void;
  // Other actions
  sendMessage: (message: Record<string, unknown>) => string | null;
  // Getters
  getLastMessage: () => StoryboardMessage | undefined;
  getLastResponse: () => Maybe<WebSocketMessage>;
  // TTS Cache
  getTTSCache: (
    playbackId: string,
    text: string,
    voiceId?: string
  ) => TTSCacheEntry | null;
  invalidateTTSCache: (playbackId: string) => void;
}

type StoryboardWsStore = StoryboardWsState & StoryboardWsActions;

const MAX_RECONNECT_ATTEMPTS = 10;
// Exponential backoff: 3s, 6s, 12s, … capped at 60s (~6.5 min over 10 attempts)
const RECONNECT_INTERVAL = 3000;
const MAX_RECONNECT_INTERVAL = 60_000;
// A drop after at least this long connected starts a new reconnect cycle
// (attempt budget and token refresh reset). Shorter-lived connections keep
// consuming the current cycle, so an open → close loop stays bounded.
const STABLE_CONNECTION_MS = 30_000;

const reconnectDelay = (attempt: number) =>
  Math.min(RECONNECT_INTERVAL * 2 ** attempt, MAX_RECONNECT_INTERVAL);

let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

const cancelPendingReconnect = () => {
  if (reconnectTimeoutId) {
    clearTimeout(reconnectTimeoutId);
    reconnectTimeoutId = null;
  }
};

// The socket authenticates with the raw access token, which nothing refreshes
// proactively during a long editing session (the Video Editor is pure WS
// traffic, so the REST 401 → refresh path never fires). Refresh before the
// first reconnect of a cycle so we don't loop on an expired token.
// `refreshToken()` is single-flight and returns null (without throwing) for
// sessions that have no refresh token, in which case the existing token is
// reused.
//
// A failed refresh is logged but does not stop the reconnect cycle: it may be
// a transient network error, or another tab may have won a refresh-token
// rotation and already written the fresh token to localStorage — `init()`
// re-reads it. If the session really is dead, the attempt budget runs out and
// the next REST call logs the user out.
const reconnect = async (config: InitConfig, refresh: boolean) => {
  try {
    if (refresh) {
      await authService.refreshToken();
    }
  } catch (error) {
    console.error("[StoryboardWS] Token refresh failed:", error);
  }
  // disconnect() or a re-init with another config may have run meanwhile
  if (useStoryboardWsStore.getState().config !== config) return;
  useStoryboardWsStore.getState().init(config);
};

// Reconnect when the tab regains visibility if the connection was lost while
// backgrounded and the reconnect cycle has given up. While a retry is still
// scheduled, let it happen — otherwise both paths would refresh the token.
// init() reuses the stored token: if it has expired the handshake fails and
// the regular close → refresh → reconnect cycle takes over.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    const { isConnected, isConnecting, config, init } =
      useStoryboardWsStore.getState();
    if (isConnected || isConnecting || !config || reconnectTimeoutId) return;
    useStoryboardWsStore.setState(
      { connectionAttempts: 0 },
      false,
      "visibilityReconnect"
    );
    init(config);
  });
}

const isStreamingTTSAction = (
  action?: string,
  historyAction?: string
): boolean => {
  return (
    action === "TextToSpeechStreaming" ||
    historyAction === "TextToSpeechStreaming"
  );
};

const createTTSChunk = (message: StoryboardWsMessage): StreamingAudioChunk => ({
  chunk_index: message.chunk_index ?? 0,
  speech: message.speech ?? "",
  is_final: message.is_final ?? false,
  query_id: message.query_id ?? "",
  section_id: message.section_id ?? "",
});

const shouldProcessTTSChunk = (
  isStreaming: boolean,
  message: StoryboardWsMessage
): boolean => {
  return (
    isStreaming &&
    !!message.query_id &&
    (!!message.speech || !!message.is_final)
  );
};

const initialState: StoryboardWsState = {
  ws: null,
  isConnected: false,
  isConnecting: false,
  isBusy: false,
  ttsSubscribers: new Map(),
  messageHandlers: new Set(),
  messageHistory: [],
  config: null,
  connectionAttempts: 0,
  connectedAt: 0,
  ttsCache: new Map(),
  queryToPlaybackId: new Map(),
};

export const useStoryboardWsStore = create<StoryboardWsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      init: (config: InitConfig) => {
        console.debug("[StoryboardWS] init called with config:", config);
        const { ws, isConnecting } = get();

        // Cancel any pending reconnect
        cancelPendingReconnect();

        // Already connected or connecting
        if (ws?.readyState === WebSocket.OPEN || isConnecting) {
          console.debug("[StoryboardWS] Already connected or connecting");
          return;
        }

        // Close existing connection if any
        if (ws) {
          ws.close();
        }

        // Without a token there is nothing to reconnect with either, so don't
        // store the config (it would make the visibility handler retry).
        const token = authService.getAccessToken();
        if (!token) {
          console.error("[StoryboardWS] No access token available");
          return;
        }

        // Store config for reconnection
        set({ isConnecting: true, config }, false, "init:connecting");

        let newWs: WebSocket;
        try {
          newWs = new WebSocket(`${STORYBOARD_WS_URL}?token=${token}`);
        } catch (error) {
          // An invalid URL (e.g. missing STORYBOARD_WS_URL) throws synchronously
          console.error("[StoryboardWS] Failed to open connection:", error);
          set({ isConnecting: false }, false, "init:failed");
          return;
        }

        newWs.onopen = () => {
          console.debug("[StoryboardWS] Connection opened");
          // connectionAttempts is deliberately not reset here — see onclose.
          set(
            {
              ws: newWs,
              isConnected: true,
              isConnecting: false,
              connectedAt: Date.now(),
            },
            false,
            "open"
          );

          // Send SelectDocument action to initialize
          const initMessage = {
            query_id: nanoid(),
            action: "SelectDocument",
            organization_id: config.organizationId,
            document_id: config.documentIds[0],
            project_id: config.projectId,
            storyboard_id: config.storyboardId,
            bearer_token: token,
          };
          newWs.send(JSON.stringify(initMessage));
        };

        newWs.onmessage = ({ data: rawData }) => {
          try {
            const data = JSON.parse(rawData as string);
            console.debug("[StoryboardWS] onmessage", data);
            const { ttsSubscribers, messageHandlers, messageHistory } = get();

            const existingMessageIndex = messageHistory.findIndex(
              (message) => message.query_id === data.query_id
            );

            if (existingMessageIndex < 0) {
              return;
            }

            const existingMessage = messageHistory[existingMessageIndex];
            const isStreamingAction = isStreamingTTSAction(
              data.action,
              existingMessage?.action
            );

            const updatedMessage: StoryboardMessage = {
              ...existingMessage,
              ...data,
              is_final: isStreamingAction ? data.is_final : true,
            };

            const newMessageHistory = [...messageHistory];
            newMessageHistory[existingMessageIndex] = updatedMessage;

            set(
              {
                messageHistory: newMessageHistory,
                isBusy: newMessageHistory.some((x) => x.is_final === false),
              },
              false,
              "message:update"
            );

            // Route TTS chunks to subscribers and cache
            if (shouldProcessTTSChunk(isStreamingAction, updatedMessage)) {
              const chunk = createTTSChunk(updatedMessage);

              // Route to subscriber if exists
              const handler = ttsSubscribers.get(updatedMessage.query_id);
              handler?.(chunk);

              // Store chunk in cache
              const { ttsCache, queryToPlaybackId } = get();
              const playbackId = queryToPlaybackId.get(updatedMessage.query_id);
              const cacheEntry = playbackId ? ttsCache.get(playbackId) : null;

              if (cacheEntry) {
                const newCache = new Map(ttsCache);
                if (!playbackId) return;
                newCache.set(playbackId, {
                  ...cacheEntry,
                  chunks: [...cacheEntry.chunks, chunk],
                  isComplete: chunk.is_final,
                });
                set({ ttsCache: newCache }, false, "ttsCache:addChunk");
              }
            }

            // Notify general message handlers
            messageHandlers.forEach((handler) => {
              try {
                handler(updatedMessage as StoryboardWsMessage);
              } catch (error) {
                console.error("[StoryboardWS] Handler error:", error);
              }
            });
          } catch (error) {
            console.error("[StoryboardWS] Failed to parse message:", error);
          }
        };

        newWs.onclose = (event) => {
          console.debug(
            "[StoryboardWS] Connection closed:",
            event.code,
            event.reason
          );

          // A superseded socket (closed by disconnect() or replaced by a
          // re-init) must not touch the current connection's state. `ws` is
          // set synchronously below, so the live socket always passes.
          if (get().ws !== newWs) return;

          const {
            config: storedConfig,
            connectedAt,
            messageHistory,
            messageHandlers,
          } = get();

          // Requests in flight on this connection can't complete on the next
          // one (the server session is per connection): fail them so isBusy
          // clears and TTSProvider drops its pending playback.
          const orphaned = messageHistory
            .filter((message) => message.is_final !== true)
            .map<StoryboardMessage>((message) => ({
              ...message,
              is_final: true,
              status: "failed",
            }));
          const failedHistory = orphaned.length
            ? messageHistory.map(
                (message) =>
                  orphaned.find((o) => o.query_id === message.query_id) ??
                  message
              )
            : messageHistory;

          set(
            {
              ws: null,
              isConnected: false,
              isConnecting: false,
              isBusy: false,
              connectedAt: 0,
              messageHistory: failedHistory,
            },
            false,
            "close"
          );

          orphaned.forEach((message) => {
            messageHandlers.forEach((handler) => {
              try {
                handler(message as StoryboardWsMessage);
              } catch (error) {
                console.error("[StoryboardWS] Handler error:", error);
              }
            });
          });

          // Every close reaching this point is server- or network-initiated
          // (our own disconnect()/re-init replace `ws` before their close
          // event lands, see the guard above), so always try to reconnect.
          if (!storedConfig) return;

          // A connection that stayed up long enough counts as recovered: this
          // drop starts a fresh cycle. Otherwise (never opened, or died right
          // after opening) keep counting so an open → close loop gives up.
          const wasStable =
            connectedAt > 0 && Date.now() - connectedAt >= STABLE_CONNECTION_MS;
          const connectionAttempts = wasStable ? 0 : get().connectionAttempts;

          if (connectionAttempts >= MAX_RECONNECT_ATTEMPTS) {
            console.error(
              "[StoryboardWS] Max reconnect attempts reached, giving up"
            );
            return;
          }

          // Refresh the token only on the first attempt of a cycle; later
          // attempts reuse it so a flaky network doesn't rotate the refresh
          // token on every retry.
          const shouldRefresh = connectionAttempts === 0;
          const delay = reconnectDelay(connectionAttempts);
          console.debug(
            `[StoryboardWS] Scheduling reconnect attempt ${connectionAttempts + 1}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`,
            { willRefreshToken: shouldRefresh }
          );
          set(
            { connectionAttempts: connectionAttempts + 1 },
            false,
            "reconnect:schedule"
          );
          reconnectTimeoutId = setTimeout(() => {
            reconnectTimeoutId = null;
            void reconnect(storedConfig, shouldRefresh);
          }, delay);
        };

        newWs.onerror = (error) => {
          console.error("[StoryboardWS] Connection error:", error);
        };

        set({ ws: newWs }, false, "init:ws");
      },

      disconnect: () => {
        // Cancel any pending reconnect
        cancelPendingReconnect();

        const { ws } = get();
        if (ws) {
          ws.close(1000, "Client disconnect");
        }
        set(
          {
            ws: null,
            isConnected: false,
            isConnecting: false,
            isBusy: false,
            ttsSubscribers: new Map(),
            messageHistory: [],
            config: null,
            connectionAttempts: 0,
            connectedAt: 0,
          },
          false,
          "disconnect"
        );
      },

      requestTextToSpeech: ({
        sectionId,
        slideId,
        text,
        voiceId,
        cacheKey,
      }: TTSRequestOptions) => {
        const { ws, isConnected, messageHistory, ttsCache, queryToPlaybackId } =
          get();
        const playbackId = cacheKey ?? slideId ?? sectionId;

        if (!ws || !isConnected) {
          console.error(
            "[StoryboardWS] Not connected, cannot send TTS request"
          );
          return null;
        }

        const queryId = nanoid();
        const data = {
          action: "TextToSpeechStreaming",
          query_id: queryId,
          section_id: sectionId,
          slide_id: slideId,
          text,
        };

        // Initialize cache entry for this playback and map queryId to playbackId
        if (playbackId && text) {
          const newCache = new Map(ttsCache);
          newCache.set(playbackId, {
            text,
            chunks: [],
            isComplete: false,
            voiceId,
          });
          const newQueryToPlaybackId = new Map(queryToPlaybackId);
          newQueryToPlaybackId.set(queryId, playbackId);
          set(
            { ttsCache: newCache, queryToPlaybackId: newQueryToPlaybackId },
            false,
            "requestTTS:initCache"
          );
        }

        // Add to message history (same as old hook's addMessage)
        set(
          {
            messageHistory: [
              ...messageHistory,
              { ...data } as unknown as StoryboardMessage,
            ],
            isBusy: true,
          },
          false,
          "requestTTS"
        );

        ws.send(JSON.stringify(data));

        return queryId;
      },

      subscribeToTTS: (queryId: string, handler: TTSChunkHandler) => {
        set(
          (state) => {
            const newSubscribers = new Map(state.ttsSubscribers);
            newSubscribers.set(queryId, handler);
            return { ttsSubscribers: newSubscribers };
          },
          false,
          `subscribeToTTS:${queryId}`
        );

        // Return unsubscribe function
        return () => {
          set(
            (state) => {
              const newSubscribers = new Map(state.ttsSubscribers);
              newSubscribers.delete(queryId);
              return { ttsSubscribers: newSubscribers };
            },
            false,
            `unsubscribeFromTTS:${queryId}`
          );
        };
      },

      addMessageHandler: (handler: MessageHandler) => {
        set(
          (state) => {
            const newHandlers = new Set(state.messageHandlers);
            newHandlers.add(handler);
            return { messageHandlers: newHandlers };
          },
          false,
          "addMessageHandler"
        );

        return () => {
          set(
            (state) => {
              const newHandlers = new Set(state.messageHandlers);
              newHandlers.delete(handler);
              return { messageHandlers: newHandlers };
            },
            false,
            "removeMessageHandler"
          );
        };
      },

      sendMessage: (message: Record<string, unknown>) => {
        const { ws, isConnected } = get();

        if (!ws || !isConnected) {
          console.error("[StoryboardWS] Not connected");
          return null;
        }

        const queryId = nanoid();

        ws.send(JSON.stringify({ ...message, query_id: queryId }));
        set({ isBusy: true }, false, "sendMessage");

        return queryId;
      },

      requestGenerateScript: (storyboard: unknown, sectionId: string) => {
        const { ws, isConnected, messageHistory } = get();

        if (!ws || !isConnected) {
          console.error("[StoryboardWS] Not connected");
          return null;
        }

        const queryId = nanoid();

        const data = {
          action: "GenerateScript",
          query_id: queryId,
          storyboard,
          section_id: sectionId,
        };

        set(
          {
            messageHistory: [
              ...messageHistory,
              { ...data, is_final: false } as StoryboardMessage,
            ],
            isBusy: true,
          },
          false,
          "requestGenerateScript"
        );
        ws.send(JSON.stringify(data));

        return queryId;
      },

      requestImproveScript: (
        sectionId: string,
        script: TScript,
        message: string
      ) => {
        const { ws, isConnected, messageHistory } = get();

        if (!ws || !isConnected) {
          console.error("[StoryboardWS] Not connected");
          return null;
        }

        const queryId = nanoid();

        const data = {
          action: "ImproveScript",
          query_id: queryId,
          script,
          message,
          section_id: sectionId,
        };

        set(
          {
            messageHistory: [
              ...messageHistory,
              { ...data, is_final: false } as unknown as StoryboardMessage,
            ],
            isBusy: true,
          },
          false,
          "requestImproveScript"
        );
        ws.send(JSON.stringify(data));

        return queryId;
      },

      requestCreateSection: (topic: string, storyboard: unknown) => {
        const { ws, isConnected, messageHistory } = get();

        if (!ws || !isConnected) {
          console.error("[StoryboardWS] Not connected");
          return null;
        }

        const queryId = nanoid();

        const data = {
          action: "CreateSection",
          query_id: queryId,
          topic,
          storyboard,
        };

        set(
          {
            messageHistory: [
              ...messageHistory,
              { ...data, is_final: false } as StoryboardMessage,
            ],
            isBusy: true,
          },
          false,
          "requestCreateSection"
        );
        ws.send(JSON.stringify(data));

        return queryId;
      },

      requestUpdateSection: (
        sectionId: string,
        sectionUpdate: SectionUpdate
      ) => {
        const { ws, isConnected, messageHistory } = get();

        if (!ws || !isConnected) {
          console.error("[StoryboardWS] Not connected");
          return null;
        }

        const queryId = nanoid();

        const data = {
          action: "UpdateSection",
          query_id: queryId,
          section_id: sectionId,
          section_update: sectionUpdate,
        };

        set(
          {
            messageHistory: [
              ...messageHistory,
              { ...data, is_final: false } as unknown as StoryboardMessage,
            ],
            isBusy: true,
          },
          false,
          "requestUpdateSection"
        );
        ws.send(JSON.stringify(data));

        return queryId;
      },

      requestRegenerateSection: (
        sectionId: string,
        topic: string,
        storyboard: unknown
      ) => {
        const { ws, isConnected, messageHistory } = get();

        if (!ws || !isConnected) {
          console.error("[StoryboardWS] Not connected");
          return null;
        }

        const queryId = nanoid();

        const data = {
          action: "RegenerateSection",
          query_id: queryId,
          topic,
          section_id: sectionId,
          storyboard,
        };

        set(
          {
            messageHistory: [
              ...messageHistory,
              { ...data, is_final: false } as unknown as StoryboardMessage,
            ],
            isBusy: true,
          },
          false,
          "requestRegenerateSection"
        );
        ws.send(JSON.stringify(data));

        return queryId;
      },

      requestRetrieveSectionData: (id: string) => {
        const { ws, isConnected, messageHistory } = get();

        if (!ws || !isConnected) {
          console.error("[StoryboardWS] Not connected");
          return null;
        }

        const queryId = nanoid();

        const data = {
          action: "RetrieveSectionData",
          query_id: queryId,
          section_id: id,
        };

        set(
          {
            messageHistory: [
              ...messageHistory,
              { ...data, is_final: false } as unknown as StoryboardMessage,
            ],
            isBusy: true,
          },
          false,
          "requestRetrieveSectionData"
        );
        ws.send(JSON.stringify(data));

        return queryId;
      },

      requestComputeMetrics: (storyboard: unknown) => {
        const { ws, isConnected, messageHistory } = get();

        if (!ws || !isConnected) {
          console.error("[StoryboardWS] Not connected");
          return null;
        }

        const queryId = nanoid();

        const data = {
          action: "ComputeMetrics",
          query_id: queryId,
          storyboard,
        };

        set(
          {
            messageHistory: [
              ...messageHistory,
              { ...data, is_final: false } as StoryboardMessage,
            ],
            isBusy: true,
          },
          false,
          "requestComputeMetrics"
        );
        ws.send(JSON.stringify(data));

        return queryId;
      },

      getLastMessage: () => {
        const { messageHistory } = get();
        return messageHistory[messageHistory.length - 1];
      },

      getLastResponse: () => {
        const { messageHistory } = get();
        const lastMessage = messageHistory[messageHistory.length - 1];
        return (
          lastMessage?.is_final === true ? lastMessage : null
        ) as Maybe<WebSocketMessage>;
      },

      getTTSCache: (playbackId: string, text: string, voiceId?: string) => {
        const { ttsCache } = get();
        const entry = ttsCache.get(playbackId);
        // Return cache only if text + voice match and we have chunks
        if (
          entry?.text === text &&
          entry.voiceId === voiceId &&
          entry.chunks.length > 0
        ) {
          return entry;
        }
        return null;
      },

      invalidateTTSCache: (playbackId: string) => {
        const { ttsCache } = get();
        if (ttsCache.has(playbackId)) {
          const newCache = new Map(ttsCache);
          newCache.delete(playbackId);
          set({ ttsCache: newCache }, false, "ttsCache:invalidate");
        }
      },
    }),
    { name: "StoryboardWsStore" }
  )
);
